import React, { useState } from 'react';
import { Flag, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { toast } from 'sonner';

/**
 * Compact "Report" button + dialog.
 * Props: targetId (required), targetName?, context? (string), variant ('pill' | 'icon')
 */
export default function ReportButton({ targetId, targetName, context, variant = 'pill' }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  if (!user || user === false || user.id === targetId) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) { toast.error(t('report.too_short')); return; }
    setLoading(true);
    try {
      await api.post('/reports', { target_id: targetId, reason: reason.trim(), context });
      toast.success(t('report.sent'));
      setOpen(false);
      setReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`report-btn-${targetId}`}
        onClick={() => setOpen(true)}
        className={variant === 'icon'
          ? 'inline-grid place-items-center w-7 h-7 rounded-full text-white/40 hover:text-red-300 hover:bg-red-500/10 transition'
          : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/5 text-white/60 hover:bg-red-500/10 hover:text-red-300 border border-white/10 transition'}
        title={t('report.title')}
      >
        <Flag className={variant === 'icon' ? 'w-3.5 h-3.5' : 'w-3.5 h-3.5'} />
        {variant === 'pill' && <span>{t('report.title')}</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass-strong border-white/10 text-white max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight inline-flex items-center gap-2">
              <Flag className="w-5 h-5 text-red-400" /> {t('report.title')} {targetName ? `· ${targetName}` : ''}
            </DialogTitle>
            <DialogDescription className="text-white/60">{t('report.desc')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-white/80">{t('report.reason')}</Label>
              <Textarea
                data-testid="report-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder={t('report.placeholder')}
                className="bg-black/40 border-white/10 text-white placeholder:text-white/30 resize-none"
              />
              <div className="text-[10px] text-white/40 mt-1">{reason.length}/500</div>
            </div>
            <Button
              type="submit"
              data-testid="report-submit"
              disabled={loading || reason.trim().length < 5}
              className="w-full bg-red-500 hover:bg-red-600 text-white font-bold uppercase tracking-wide rounded-full"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('report.submit')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
